import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { StatisticsRow } from '../../types/supplierStatistics';
import SmartImage from '../SmartImage';
import StatisticsChannels from './StatisticsChannels';
import { Delta, money, number, percent, StockStatus } from './StatisticsTable';

export function productCaption(row: StatisticsRow) {
  return `${row.productNumber || ''} · ${row.colorName || row.attribute1Code || (row.isProductAggregate ? 'Alla färger' : 'Saknad färgkoppling')}`;
}

export default function StatisticsProducts({
  rows,
  href,
  preview = false,
}: {
  rows: StatisticsRow[];
  href: (row: StatisticsRow) => string;
  preview?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!rows.length)
    return (
      <Paper variant="outlined" sx={{ p: 3, color: 'text.secondary' }}>
        Inga produkter för valda filter.
      </Paper>
    );
  return (
    <Box>
      <Grid container spacing={2}>
        {(preview && !expanded ? rows.slice(0, 4) : rows).map((row, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            key={`${row.productId}-${row.attribute1Code}-${index}`}
          >
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                transition: 'border-color 150ms',
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <Box
                component={RouterLink}
                to={href(row)}
                aria-label={`Visa ${row.productName || row.productNumber}`}
                sx={{
                  display: 'block',
                  height: 190,
                  p: 2,
                  bgcolor: '#f8fafc',
                  '&:focus-visible': {
                    outline: '3px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: -3,
                  },
                }}
              >
                <SmartImage
                  src={row.imageUrl}
                  alt={row.productName || row.productNumber}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <CardContent>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ lineHeight: 1.5 }}
                >
                  {row.brandName}
                </Typography>
                <Link
                  component={RouterLink}
                  to={href(row)}
                  underline="hover"
                  color="text.primary"
                  sx={{ display: 'block', fontWeight: 700, mt: 0.5 }}
                >
                  {row.productName || row.productNumber}
                </Link>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 1.5 }}
                >
                  {productCaption(row)}
                </Typography>
                {row.missingArticle && (
                  <Chip
                    size="small"
                    label="Artikelkoppling saknas"
                    sx={{ mb: 1 }}
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  Omsättning vald period
                </Typography>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                  gap={1}
                  flexWrap="wrap"
                >
                  <Typography variant="h6" fontWeight={700}>
                    {money(row.totalRevenue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {number(row.totalUnitsSold)} antal¹
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    my: 1.5,
                    py: 1.5,
                    borderTop: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <StatisticsChannels row={row} compact />
                </Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Tillgängligt lager:{' '}
                  {row.stockKnown ? number(row.availableStock) : '—'}
                </Typography>
                <StockStatus row={row} />
                <Box
                  component="details"
                  sx={{
                    mt: 1.5,
                    '& summary': {
                      cursor: 'pointer',
                      color: 'primary.main',
                      fontSize: 13,
                      py: 0.5,
                    },
                    '& p': { mt: 1 },
                  }}
                >
                  <summary>Jämförelse och marginal</summary>
                  <Typography variant="body2">
                    Omsättning föregående år:{' '}
                    {row.hasComparisonObservations
                      ? money(row.previousRevenue)
                      : 'Ingen observerad historik'}
                  </Typography>
                  {row.hasComparisonObservations && (
                    <Delta value={row.revenueChange} />
                  )}
                  <Typography variant="body2">
                    Antal¹ föregående år:{' '}
                    {row.hasComparisonObservations
                      ? number(row.previousUnitsSold)
                      : '—'}
                  </Typography>
                  {row.hasComparisonObservations && (
                    <Delta value={row.unitsChange} units />
                  )}
                  <Typography variant="body2">
                    Butik: {number(row.retailUnitsSold)} nettorader · Online:{' '}
                    {number(row.onlineUnitsSold)} beställda enheter
                  </Typography>
                  <Typography variant="body2">
                    Kalkylmarginal ex moms:{' '}
                    {percent(row.calculationMarginPercent)}
                  </Typography>
                  <Typography variant="body2">
                    Periodmarginal (uppskattning):{' '}
                    {percent(row.periodMarginEstimatePercent)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {preview && rows.length > 4 && (
        <Button onClick={() => setExpanded(!expanded)} sx={{ mt: 2 }}>
          {expanded
            ? 'Visa färre produkter'
            : `Visa alla ${rows.length} i urvalet`}
        </Button>
      )}
    </Box>
  );
}
